/**
 * DiscussionTimer.js
 *
 * Displays a large, centered countdown timer during discussion phases.
 * ------------------------------------------------------------
 * - Uses discussionTimes = [10, 15, 15] minutes for Stage 2, 4, and 6 respectively.
 * - Converts stage → index as:
 *      Stage 2 → idx 0
 *      Stage 4 → idx 1
 *      Stage 6+ → idx 2
 * - Automatically counts down in real-time and updates every second.
 * - Uses finishAt array from the room document for synced end time.
 * - All clients display the same countdown.
 * - Only the admin (isAdmin = true) updates stage when time runs out.
 */

import React, { useEffect, useState } from "react";
import { onSnapshot, doc, updateDoc } from "firebase/firestore";
import { db } from "../../../config/firebase";
import { TrustIssuesRoomCollection } from "../../../assets/DatabaseCollections";
import { formatTime } from "../../../BasicHelpers/FormatTimestamps";

const DiscussionTimer = ({ roomID, stage, user, isAdmin, setStage }) => {
    const [secondsLeft, setSecondsLeft] = useState(null);

    useEffect(() => {
        if (!roomID) return;

        const roomRef = doc(db, TrustIssuesRoomCollection, roomID);
        let interval = null;

        // 🔁 Listen for the latest finishAt timestamp
        const unsub = onSnapshot(roomRef, (snap) => {
            const data = snap.data();
            const finishAtArr = data?.finishAt || [];

            if (finishAtArr.length === 0) return;

            const latestFinish = finishAtArr[finishAtArr.length - 1];
            const endTime =
                latestFinish?.toDate?.() || // Firestore Timestamp
                new Date(latestFinish); // JS Date fallback

            clearInterval(interval);

            // 🕒 Start synced countdown
            interval = setInterval(async () => {
                const remaining = Math.max(
                    0,
                    Math.floor((endTime - Date.now()) / 1000)
                );
                setSecondsLeft(remaining);

                // ⏰ When time runs out → only admin updates stage
                if (remaining <= 0) {
                    clearInterval(interval);
                    if (isAdmin) {
                        try {
                            await updateDoc(roomRef, { stage: stage + 1 });
                        } catch (error) {
                            console.error("Error advancing stage:", error);
                        }
                    }
                    setStage(stage + 1);
                }
            }, 1000);
        });

        return () => {
            unsub && unsub();
            interval && clearInterval(interval);
        };

        // eslint-disable-next-line
    }, [roomID, stage, user, isAdmin]);

    return (
        <div className="w-full min-h-screen bg-black flex flex-col items-center justify-center text-white">
            <p className="text-zinc-400 text-lg sm:text-2xl mb-3">
                Discussion Time Remaining
            </p>
            <h1
                className="text-6xl sm:text-8xl md:text-9xl font-extrabold tracking-wider text-center"
                style={{ textShadow: "0 0 25px rgba(56,189,248,0.5)" }}
            >
                {secondsLeft !== null ? formatTime(secondsLeft) : "--:--"}
            </h1>

            {/* Optional progress bar */}
            <div className="w-3/4 sm:w-1/2 h-2 mt-8 bg-zinc-800 rounded-full overflow-hidden">
                <div
                    className="h-full bg-sky-400 transition-all duration-1000 ease-linear"
                    style={{
                        width: secondsLeft
                            ? `${
                                  ((secondsLeft /
                                      Math.max(
                                          secondsLeft,
                                          (secondsLeft + 1) // prevents div/0
                                      )) *
                                      100) %
                                  100
                              }%`
                            : "0%",
                    }}
                />
            </div>
        </div>
    );
};

export default DiscussionTimer;